import React, { useState, useEffect } from 'react';
import './styles/GlobalStyles.css';
import AdminDashboard from './components/AdminDashboard';
import HostScreen from './components/HostScreen';
import PlayerPortal from './components/PlayerPortal';

// URL-based routing: ?mode=host or ?mode=player&pin=XXXXXX
// Domain-based defaults: play.* → join, quiz-admin.* → admin
function getInitialState() {
  const params = new URLSearchParams(window.location.search);
  const mode = params.get('mode');
  const pin = params.get('pin') || '';
  if (mode === 'host') return { view: 'host', pin };
  if (mode === 'player') return { view: pin ? 'player' : 'join', pin };
  const host = window.location.hostname;
  if (host.startsWith('play.')) return { view: 'join', pin: '' };
  if (host.startsWith('quiz-admin.')) return { view: 'admin', pin: '' };
  return { view: 'menu', pin: '' };
}

export default function App() {
  const [{ view, pin }, setState] = useState(getInitialState);
  const [questions, setQuestions] = useState([]);

  const setView = (v) => setState(s => ({ ...s, view: v }));
  const setPin = (p) => setState(s => ({ ...s, pin: p }));

  // Keep URL in sync
  useEffect(() => {
    const params = new URLSearchParams();
    if (view === 'host') { params.set('mode', 'host'); if (pin) params.set('pin', pin); }
    if (view === 'player') { params.set('mode', 'player'); params.set('pin', pin); }
    if (view === 'join') { params.set('mode', 'player'); }
    const qs = params.toString();
    window.history.replaceState(null, '', qs ? `?${qs}` : window.location.pathname);
  }, [view, pin]);

  if (view === 'admin') {
    return <AdminDashboard questions={questions} setQuestions={setQuestions} onBack={() => setView('menu')} onLaunched={(p) => { setPin(p); setView('host'); }} />;
  }
  if (view === 'host') {
    return <HostScreen gamePin={pin} onBack={() => { setPin(''); setView('menu'); }} />;
  }
  if (view === 'player') {
    return <PlayerPortal gamePin={pin} onBack={() => { setPin(''); setView('join'); }} />;
  }
  if (view === 'join') {
    return <JoinPage onJoin={(p) => { setPin(p); setView('player'); }} onBack={() => setView('menu')} />;
  }

  // Main menu
  return (
    <div className="page-center">
      <div className="container" style={{ maxWidth: 480 }}>
        <div className="card card--hero text-center mb-24" style={{ marginBottom: 24 }}>
          <div className="pin-label" style={{ marginBottom: 8 }}>Live Quiz Platform</div>
          <div className="page-title" style={{ color: 'white', marginBottom: 8 }}>QuizSync</div>
          <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '1rem' }}>
            Create, host, and join real-time interactive quizzes
          </p>
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <div className="section-title" style={{ marginBottom: 4 }}>Host a Session</div>
          <p className="muted" style={{ marginBottom: 16 }}>Build questions and present to your audience live.</p>
          <button className="btn btn--primary btn--full" onClick={() => setView('admin')}>
            Create &amp; Host Quiz
          </button>
        </div>

        <div className="card">
          <div className="section-title" style={{ marginBottom: 4 }}>Join a Session</div>
          <p className="muted" style={{ marginBottom: 16 }}>Enter a PIN to join a live quiz as a participant.</p>
          <button className="btn btn--ghost btn--full" onClick={() => setView('join')}>
            Join with PIN
          </button>
        </div>

        <p className="muted text-center" style={{ marginTop: 24, fontSize: '0.8rem' }}>
          Share <strong>?mode=player</strong> link for players &bull; <strong>?mode=host&amp;pin=XXXXXX</strong> for hosts
        </p>
      </div>
    </div>
  );
}

function JoinPage({ onJoin, onBack }) {
  const [pin, setPin] = useState('');

  const handleJoin = () => {
    const clean = pin.trim().toUpperCase();
    if (clean.length < 6) return alert('Please enter a valid 6-character PIN.');
    onJoin(clean);
  };

  return (
    <div className="page-center">
      <div className="container" style={{ maxWidth: 420 }}>
        {!window.location.hostname.startsWith('play.') && (
          <button className="btn btn--ghost btn--sm" style={{ marginBottom: 24 }} onClick={onBack}>← Back</button>
        )}
        <div className="card text-center">
          <div className="section-title" style={{ marginBottom: 4 }}>Join a Quiz</div>
          <p className="muted" style={{ marginBottom: 24 }}>Enter the PIN shown on the host screen.</p>
          <input
            className="input input--pin"
            type="text"
            maxLength={6}
            placeholder="XXXXXX"
            value={pin}
            onChange={(e) => setPin(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
            autoFocus
          />
          <button className="btn btn--success btn--full btn--lg" style={{ marginTop: 16 }} onClick={handleJoin}>
            Join Game
          </button>
        </div>
      </div>
    </div>
  );
}
